/**
 * Document Auto-Numbering Utility
 *
 * Generates sequential document numbers using Firestore transactions.
 * Format: PREFIX-YYYY-NNNNN (e.g., PO-2024-00001)
 *
 * Uses the documentCounters sub-collection under each company.
 */

import { doc, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';

export const DOCUMENT_PREFIXES = {
  purchaseOrder: 'PO-',
  grn: 'GRN-',
  purchaseInvoice: 'PI-',
  purchaseReturn: 'PR-',
  quotation: 'QT-',
  saleOrder: 'SO-',
  deliveryChallan: 'DC-',
  saleInvoice: 'SI-',
  saleReturn: 'SR-',
  purchaseRequisition: 'PRQ-',
  inwardGatePass: 'IGP-',
  outwardGatePass: 'OGP-',
  billOfMaterials: 'BOM-',
  productionOrder: 'PROD-',
  addStock: 'ADS-',
  reduceStock: 'RDS-',
  stockTransfer: 'ST-',
  cashReceipt: 'CR-',
  bankReceipt: 'BR-',
  cashPayment: 'CP-',
  bankPayment: 'BP-',
  generalJournal: 'JV-',
};

/**
 * Get the next document number using a Firestore transaction
 *
 * @param {string} companyId - The company ID
 * @param {string} documentType - One of the keys in DOCUMENT_PREFIXES
 * @param {string} [branchId] - Optional branch ID for branch-wise counters
 * @returns {Promise<string>} The formatted document number (e.g., "PO-2024-00001")
 */
export const getNextDocumentNumber = async (companyId, documentType, branchId = null) => {
  const prefix = DOCUMENT_PREFIXES[documentType];
  if (!prefix) {
    throw new Error(`Unknown document type: ${documentType}`);
  }

  // Branch-wise counter key: "purchaseOrder__branchXYZ" or just "purchaseOrder"
  const counterKey = branchId ? `${documentType}__${branchId}` : documentType;
  const counterRef = doc(db, 'companies', companyId, 'documentCounters', counterKey);

  const result = await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);

    let nextNumber = 1;

    if (counterDoc.exists()) {
      nextNumber = counterDoc.data().nextNumber || 1;
    }

    transaction.set(counterRef, {
      nextNumber: nextNumber + 1,
      prefix: prefix,
      updatedAt: new Date().toISOString()
    }, { merge: true });

    const year = new Date().getFullYear();
    const paddedNumber = String(nextNumber).padStart(5, '0');
    return `${prefix}${year}-${paddedNumber}`;
  });

  return result;
};

/**
 * Format currency amount for display
 *
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
// Reads decimal places from the active company (published to window by
// AuthContext) so every place using formatAmount honours Currency Settings.
// Falls back to 2 when the window is not initialised yet (SSR / pre-login).
export const formatAmount = (amount, decimalPlaces) => {
  const dp = Number.isFinite(Number(decimalPlaces))
    ? Number(decimalPlaces)
    : (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__))
      ? Number(window.__AMAL_DECIMAL_PLACES__)
      : 2);
  if (amount == null || isNaN(amount)) {
    return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  }
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const documentNumbering = {
  getNextDocumentNumber,
  formatAmount,
  DOCUMENT_PREFIXES
};

export default documentNumbering;
